/** Client-side column menu helpers (sort / filter / group). */

export const FILTER_OPERATORS = [
  { value: 'eq', label: 'Is equal to' },
  { value: 'neq', label: 'Is not equal to' },
  { value: 'startswith', label: 'Starts with' },
  { value: 'endswith', label: 'Ends with' },
  { value: 'contains', label: 'Contains' },
  { value: 'doesnotcontain', label: 'Does not contain' },
  { value: 'isempty', label: 'Is empty' },
  { value: 'isnotempty', label: 'Is not empty' },
];

export const emptyColumnFilter = () => ({
  logic: 'and',
  filters: [
    { operator: 'eq', value: '' },
    { operator: 'eq', value: '' },
  ],
});

const toText = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const matchOperator = (cellValue, operator, filterValue) => {
  const text = toText(cellValue).toLowerCase();
  const needle = toText(filterValue).toLowerCase();
  const emptyOps = ['isempty', 'isnotempty'];

  if (!emptyOps.includes(operator) && !needle) return true;

  switch (operator) {
    case 'eq':
      return text === needle;
    case 'neq':
      return text !== needle;
    case 'startswith':
      return text.startsWith(needle);
    case 'endswith':
      return text.endsWith(needle);
    case 'contains':
      return text.includes(needle);
    case 'doesnotcontain':
      return !text.includes(needle);
    case 'isempty':
      return text.trim() === '';
    case 'isnotempty':
      return text.trim() !== '';
    default:
      return true;
  }
};

const isFilterActive = (filter) => {
  if (!filter?.filters?.length) return false;
  return filter.filters.some((f) => {
    if (f.operator === 'isempty' || f.operator === 'isnotempty') return true;
    return toText(f.value).trim() !== '';
  });
};

export const columnHasActiveFilter = (filtersByField, field) =>
  isFilterActive(filtersByField?.[field]);

const applyColumnFilter = (row, field, filter) => {
  if (!isFilterActive(filter)) return true;
  const active = filter.filters.filter((f) => {
    if (f.operator === 'isempty' || f.operator === 'isnotempty') return true;
    return toText(f.value).trim() !== '';
  });
  if (active.length === 0) return true;

  const results = active.map((f) => matchOperator(row[field], f.operator, f.value));
  return filter.logic === 'or' ? results.some(Boolean) : results.every(Boolean);
};

export const applyColumnFilters = (rows, filtersByField) => {
  if (!filtersByField || Object.keys(filtersByField).length === 0) return rows;
  return rows.filter((row) =>
    Object.entries(filtersByField).every(([field, filter]) =>
      applyColumnFilter(row, field, filter)
    )
  );
};

export const applySort = (rows, sortField, sortDirection) => {
  if (!sortField) return rows;
  const dir = sortDirection === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * dir;
    }
    const cmp = toText(aVal).localeCompare(toText(bVal), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    return cmp * dir;
  });
};

export const applyQuickSearch = (rows, searchTerm, fields) => {
  const term = (searchTerm || '').trim().toLowerCase();
  if (!term) return rows;
  return rows.filter((row) =>
    fields.some((field) => toText(row[field]).toLowerCase().includes(term))
  );
};

export const groupRows = (rows, groupField) => {
  if (!groupField) {
    return [{ key: null, label: null, rows }];
  }
  const map = new Map();
  rows.forEach((row) => {
    const key = toText(row[groupField]) || '(Blank)';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(([key, groupRowsList]) => ({ key, label: key, rows: groupRowsList }));
};

/**
 * Full client pipeline: search → column filters → sort → group
 */
export const processClientRows = ({
  rows,
  searchTerm,
  searchFields,
  filtersByField,
  sortField,
  sortDirection,
  groupField,
}) => {
  let next = applyQuickSearch(rows, searchTerm, searchFields);
  next = applyColumnFilters(next, filtersByField);
  next = applySort(next, sortField, sortDirection);
  return groupRows(next, groupField);
};
